import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldAlert, Clock } from 'lucide-react';

export default function ProtectedRoute({ children, requiredRole = null, requiredPermission = null }) {
  const { user, profile, isLoading, isInitialized } = useAuth();
  const location = useLocation();

  console.log('ProtectedRoute - isInitialized:', isInitialized, 'isLoading:', isLoading, 'user:', user, 'profile:', profile);

  // Show loading while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-center">
          <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
          <Skeleton className="h-4 w-32 mx-auto mb-2" />
          <Skeleton className="h-3 w-24 mx-auto" />
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user profile exists and is active
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 neumorphic rounded-3xl flex items-center justify-center mx-auto mb-6 bg-yellow-100">
            <Clock className="w-12 h-12 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-neumorphic mb-4">Setting Up Profile</h2>
          <p className="text-gray-600">Your profile is being created. Please refresh the page in a moment.</p>
        </div>
      </div>
    );
  }

  if (profile.status === 'suspended') {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 neumorphic rounded-3xl flex items-center justify-center mx-auto mb-6 bg-red-100">
            <ShieldAlert className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-neumorphic mb-4">Account Suspended</h2>
          <p className="text-gray-600">Your account has been suspended. Please contact support for assistance.</p>
        </div>
      </div>
    );
  }

  if (profile.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 neumorphic rounded-3xl flex items-center justify-center mx-auto mb-6 bg-yellow-100">
            <Clock className="w-12 h-12 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-neumorphic mb-4">Account Pending Approval</h2>
          <p className="text-gray-600">Your account is being reviewed. You'll receive an email once approved.</p>
        </div>
      </div>
    );
  }

  // Check role requirements
  if (requiredRole && profile.role !== requiredRole && profile.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neumorphic mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Check specific permissions
  if (requiredPermission && !profile.hasPermission?.(requiredPermission)) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neumorphic mb-4">Insufficient Permissions</h2>
          <p className="text-gray-600">You don't have the required permissions for this action.</p>
        </div>
      </div>
    );
  }

  return children;
}